import { Copy, Grip, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/ui/design-system/components';
import InlineCardBreadcrumb, {
    BreadcrumbItems,
} from '@/ui/components/AiAssistant/visuals/primitives/InlineCardBreadcrumb';

// The report-grid frame for an inline visual: a darker-gray rounded tile (Figma
// node 2011:12196) with the breadcrumb header on top and the widget body below.
//
//  • View  — the header is the purple-bar breadcrumb; no grip, no menu.
//  • Edit   — the drag grip replaces the purple bar, an actions menu is
//             right-aligned (Figma node 2055:11852), and the tile shows a hover
//             ring (the `--ring` focus token = purple-300, matching Figma).
//
// This is injected into the generic visual as its `Frame`, so every report-only
// concern (gray tile, editing affordances, the drag source) lives here in the
// report layer — the shared visual stays surface-agnostic. The grip is the drag
// SOURCE (`dragRef` is a react-dnd connector); reorder logic lives in
// ReportWidgetGrid, and the drag preview / live index live in ReportWidget.
function ReportVisualFrame({
    breadcrumb,
    children,
    editing = false,
    dragRef,
    onEdit,
    onDuplicate,
    onRemove,
}) {
    return (
        <div
            className={`h-full min-h-[455px] flex flex-col bg-background-med-em rounded-xl overflow-clip ${
                editing ? 'hover:ring-[3px] hover:ring-ring' : ''
            }`}
        >
            {editing ? (
                <div className="flex items-center justify-between gap-2 p-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <button
                            ref={dragRef}
                            type="button"
                            aria-label="Drag to reorder"
                            className="shrink-0 cursor-grab text-muted-foreground hover:text-foreground"
                        >
                            <Grip size={16} />
                        </button>
                        <BreadcrumbItems {...breadcrumb} />
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                aria-label="Widget actions"
                                className="shrink-0 text-muted-foreground hover:text-foreground"
                            >
                                <MoreHorizontal size={16} />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => onEdit?.()}>
                                <Pencil size={14} className="mr-2" />
                                Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => onDuplicate?.()}>
                                <Copy size={14} className="mr-2" />
                                Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onSelect={() => onRemove?.()}
                            >
                                <Trash2 size={14} className="mr-2" />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            ) : (
                <div className="px-4 py-3">
                    <InlineCardBreadcrumb {...breadcrumb} />
                </div>
            )}
            {/* `[&>*]:flex-1` stretches the single body child to fill the tile below
                the header (the min-h-[455px] floor), matching the Figma flush
                gray-header / white-body tile. */}
            <div className="flex flex-1 flex-col min-h-0 [&>*]:flex-1">
                {children}
            </div>
        </div>
    );
}

export default ReportVisualFrame;
